<template>
  <NoCustomerState v-if="!selectedCustomerId" :hasCustomers="hasCustomers" />
  <div v-else class="roles-dashboard-page">
    <h4 class="tw-text-[#6d7287] tw-text-base tw-mb-5 tw-flex tw-items-center tw-justify-between">
      <span>{{ t('dashboard.titles.dashboard') }}</span>

      <div class="tw-w-[300px]">
        <DateRange
          :initial-date="[startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), new Date()]"
          @selectDate="changeDateRange($event)"
        />
      </div>
    </h4>

    <Card class="tw-p-4 tw-flex-wrap tw-mb-5">
      <div class="tw-w-full tw-flex tw-items-center tw-justify-end">
        <div class="tw-flex tw-items-center tw-gap-4">
          <i
            class="icon-menu tw-cursor-pointer tw-p-1.5 before:tw-text-text-primary before:tw-text-base"
            :class="{
              'tw-bg-primary-500 tw-text-text-primary': statisticsView === 'totalCounts',
            }"
            @click="statisticsView = 'totalCounts'"
          ></i>

          <i
            class="icon-statistics tw-cursor-pointer tw-p-1.5 before:tw-text-text-primary before:tw-text-base"
            :class="{
              'tw-bg-primary-500 tw-text-text-primary': statisticsView === 'charts',
            }"
            @click="statisticsView = 'charts'"
          ></i>
        </div>
      </div>

      <div>
        <template v-if="statisticsView === 'totalCounts'">
          <div class="tw-flex tw-items-center tw-flex-wrap tw-mx-4 tw-my-8">
            <template v-for="(property, index) in statisticsCountsProperties" :key="property">
              <div
                v-if="property.value || property.value === 0"
                class="tw-flex-1 tw-min-w-[150px] tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-4 tw-px-6 tw-text-center"
                :class="{ 'tw-border-l tw-border-base-light-gray': index > 0 }"
              >
                <span class="tw-text-[#525252] tw-text-[0.8rem]">
                  {{ property.key }}
                </span>

                <span class="tw-text-[#525252] tw-text-[1.75rem]">
                  <template v-if="property.type === 'currency'">
                    {{ n(property.value, 'currency') }}
                  </template>

                  <template v-else>
                    {{ property.value }}
                  </template>
                </span>
              </div>
            </template>
          </div>
        </template>

        <template v-if="statisticsView === 'charts'">
          <div class="tw-flex tw-items-center tw-justify-center tw-my-8 tw-gap-8 tw-flex-wrap">
            <div
              class="xl:tw-w-[40%]"
              v-if="
                currentUser?.user_role.includes(UserRoles.PURCHASER) ||
                currentUser?.user_role.includes(UserRoles.ORDER_CLEARER) ||
                currentUser?.user_role.includes(UserRoles.APPROVER) ||
                currentUser?.user_role.includes(UserRoles.VIEWER)
              "
            >
              <OrdersDonutChart
                v-if="Object.values(rolesDashboardStatistics?.order_counts as any).reduce(
                    (acc: any, curr: any) => acc + curr,
                    0
                  )"
                :ordersChartStatistics="rolesDashboardStatistics?.order_counts!"
              />

              <span v-else class="chart-placeholder">
                {{
                  t('dashboard.titles.chart_no_data', {
                    chartName: t('dashboard.titles.orders').toLowerCase(),
                  })
                }}
              </span>
            </div>

            <div
              v-if="
                !(
                  currentUser?.user_role.includes(UserRoles.APPROVER) ||
                  currentUser?.user_role.includes(UserRoles.VIEWER)
                )
              "
              class="xl:tw-w-[40%]"
            >
              <AmountsDonutChart
                v-if="
                  Object.values(rolesDashboardStatistics?.amounts as any).reduce(
                    (acc: any, curr: any) => acc + curr,
                    0
                  )
                "
                :amountsChartStatistics="rolesDashboardStatistics?.amounts!"
              />

              <span v-else class="chart-placeholder">
                {{
                  t('dashboard.titles.chart_no_data', {
                    chartName: t('dashboard.titles.amounts').toLowerCase(),
                  })
                }}
              </span>
            </div>

            <div
              class="xl:tw-w-[40%]"
              v-if="
                currentUser?.user_role.includes(UserRoles.PAYER) ||
                currentUser?.user_role.includes(UserRoles.PAYMENT_CLEARER) ||
                currentUser?.user_role.includes(UserRoles.APPROVER) ||
                currentUser?.user_role.includes(UserRoles.VIEWER)
              "
            >
              <PaymentsDonutChart
                v-if="
                 Object.values(rolesDashboardStatistics?.payment_counts as any).reduce(
                    (acc: any, curr: any) => acc + curr,
                    0
                  )
                "
                :paymentsChartStatistics="rolesDashboardStatistics?.payment_counts!"
              />

              <span v-else class="chart-placeholder">
                {{
                  t('dashboard.titles.chart_no_data', {
                    chartName: t('dashboard.titles.payments').toLowerCase(),
                  })
                }}
              </span>
            </div>
          </div>
        </template>
      </div>
    </Card>

    <div
      v-if="
        currentUser?.user_role.includes(UserRoles.PURCHASER) ||
        currentUser?.user_role.includes(UserRoles.ORDER_CLEARER) ||
        currentUser?.user_role.includes(UserRoles.APPROVER) ||
        currentUser?.user_role.includes(UserRoles.VIEWER)
      "
      class="tw-mb-4"
    >
      <OrdersFancyTable :customerId="selectedCustomerId" :isDashboard="true" />
    </div>

    <div
      v-if="
        currentUser?.user_role.includes(UserRoles.PAYER) ||
        currentUser?.user_role.includes(UserRoles.PAYMENT_CLEARER) ||
        currentUser?.user_role.includes(UserRoles.APPROVER) ||
        currentUser?.user_role.includes(UserRoles.VIEWER)
      "
      class="payments-table"
    >
      <div class="tw-flex tw-flex-col tw-h-full">
        <div class="tw-flex tw-flex-row tw-justify-end tw-px-4 tw-items-end tw-mb-4">
          <button
            @click="navigateToCreatePayment"
            class="tw-text-sm tw-font-semibold tw-text-primary-400 tw-underline hover:tw-text-primary-600 tw-transition-colors tw-duration-300"
          >
            {{ t('payment.titles.new_payment') }}
          </button>
        </div>
        <Card class="tw-p-5">
          <PaymentsFancyTable :customerId="selectedCustomerId" :isDashboard="true" />
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Card } from '@/components/ui/card';
import { useRolesDashboardCounts } from '@/_helpers/composables/dashboard/roles-dashboard-counts.composable';
import { useDashboardStore } from '@/stores/dashboard.store';
import { useUserStore } from '@/stores/user.store';
import { useCustomerStore } from '@/stores/customer.store';
import moment from 'moment';
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import OrdersFancyTable from '@/views/Orders/Table/OrdersFancyTable.vue';
import { UserRoles } from '@/_helpers/interfaces';
import OrdersDonutChart from '@/components/dashboards/OrdersDonutChart.vue';
import PaymentsDonutChart from '@/components/dashboards/PaymentsDonutChart.vue';
import AmountsDonutChart from '@/components/dashboards/AmountsDonutChart.vue';
import NoCustomerState from '@/components/shared/NoCustomerState.vue';
import type { RolesDashboardStatistics } from '@/_helpers/interfaces/dashboards/roles-dashboard-statistics.interface';
import PaymentsFancyTable from '@/views/Payments/Table/PaymentsFancyTable.vue';
import { startOfWeek, subWeeks } from 'date-fns';
import { useI18n } from 'vue-i18n';
import router from '@/router';
const { t, n } = useI18n();

const { currentUser } = useUserStore();
const { hasCustomers, selectedCustomerId } = storeToRefs(useCustomerStore());

const { getRolesDashboardStatistics } = useDashboardStore();
const { getRolesStatisticsCounts } = useRolesDashboardCounts();

const selectedDateRange = ref<{ startDate: string; endDate: string }>({
  startDate: moment(startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 })).format('YYYY-MM-DD'),
  endDate: moment(new Date()).format('YYYY-MM-DD'),
});
const statisticsView = ref('totalCounts');
const rolesDashboardStatistics = ref<RolesDashboardStatistics>();
const statisticsCountsProperties = ref<any[]>([]);

// Watch for customer changes and fetch statistics
watch(
  selectedCustomerId,
  async newCustomerId => {
    if (newCustomerId) {
      await getDashboardStatistics();
    }
  },
  { immediate: true }
);

const getDashboardStatistics = async () => {
  const { startDate, endDate } = selectedDateRange.value;

  rolesDashboardStatistics.value = await getRolesDashboardStatistics(
    startDate,
    endDate,
    selectedCustomerId.value!
  );

  statisticsCountsProperties.value = getRolesStatisticsCounts(rolesDashboardStatistics.value!);
};

const changeDateRange = async range => {
  selectedDateRange.value = { ...range };
  await getDashboardStatistics();
};

const navigateToCreatePayment = () => {
  router.push({ name: 'create-payment' });
};
</script>

<style scoped>
/* Using Tailwind classes - no additional styles needed */
</style>
